import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { env } from "@/env";
import type { OrderPaymentStatus, OrderStatus } from "@/hooks/use-orders";
import { queryKeys } from "@/lib/query-keys";

const POLL_FALLBACK_INTERVAL_MS = 5000;
const ERROR_THRESHOLD = 2;

export type OrderEvent = {
  type: "order.created" | "order.status_changed" | "order.payment_updated";
  timestamp: string;
  organizationId: string;
  orderId: string;
  data: {
    status?: OrderStatus;
    paymentStatus?: OrderPaymentStatus;
    restaurantTableId?: string | null;
  };
};

export type OrderEventScope =
  | { type: "org" }
  | { type: "order"; orderId: string }
  | { type: "board"; orgSlug: string };

function buildUrl(scope: OrderEventScope): string {
  const base = env.VITE_API_URL;
  switch (scope.type) {
    case "org":
      return `${base}/api/orders/events`;
    case "order":
      return `${base}/api/orders/${scope.orderId}/events`;
    case "board":
      return `${base}/api/orders/board/events?orgSlug=${encodeURIComponent(scope.orgSlug)}`;
    default:
      return base;
  }
}

export function useOrderEvents(scope: OrderEventScope, onEvent?: (event: OrderEvent) => void) {
  const [connected, setConnected] = useState(false);
  const [pollingFallback, setPollingFallback] = useState(false);
  const queryClient = useQueryClient();
  const errorCountRef = useRef(0);
  const onEventRef = useRef(onEvent);

  useEffect(() => {
    onEventRef.current = onEvent;
  }, [onEvent]);

  useEffect(() => {
    const url = buildUrl(scope);
    let stopped = false;
    let pollingTimer: ReturnType<typeof setInterval> | null = null;

    function startPolling() {
      if (pollingTimer) {
        return;
      }
      setPollingFallback(true);
      pollingTimer = setInterval(() => {
        queryClient.invalidateQueries({ queryKey: queryKeys.orders.all() });
      }, POLL_FALLBACK_INTERVAL_MS);
    }

    const eventSource = new EventSource(url, { withCredentials: true });

    eventSource.onopen = () => {
      setConnected(true);
      errorCountRef.current = 0;
    };

    eventSource.onmessage = (e) => {
      try {
        const parsed = JSON.parse(e.data) as OrderEvent;
        queryClient.invalidateQueries({ queryKey: queryKeys.orders.all() });
        onEventRef.current?.(parsed);
      } catch {
        // ignore parse errors
      }
    };

    eventSource.onerror = () => {
      setConnected(false);
      errorCountRef.current += 1;
      if (errorCountRef.current >= ERROR_THRESHOLD && !stopped) {
        eventSource.close();
        startPolling();
      }
    };

    return () => {
      stopped = true;
      eventSource.close();
      if (pollingTimer) {
        clearInterval(pollingTimer);
      }
    };
  }, [scope, queryClient]);

  return { connected, pollingFallback };
}
